import React, { useState } from 'react';

const HomePage: React.FC = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  React.useEffect(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openContactForm = () => {
    setFormStatus('idle');
    setShowContactForm(true);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d6cjmg6qd308q1a09jp0', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });
      
      if (response.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-right z-50 w-full transition-all duration-300 ${
        isNavScrolled ? 'bg-white shadow-md' : 'bg-white/80'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
              アドバンスポット
            </div>
            <div className="hidden md:flex space-x-8" style={{ fontFamily: 'serif' }}>
              <a href="#strengths" className="text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">強み</a>
              <a href="#warranty" className="text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">保証</a>
              <a href="#voice" className="text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">お客様の声</a>
              <a href="#contact" className="text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">お問い合わせ</a>
            </div>
          </div>
        </div>
      </nav>

      {/* First View */}
      <section className="relative min-h-screen flex items-center justify-center py-20 sm:py-24">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9f46e346ea314abbf41b76ca8b5bba3c.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 w-full">
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 md:mb-6 leading-tight" style={{ fontFamily: 'serif', letterSpacing: '0.05em' }}>
            「車が必要なのに買えない」を解消。<br />
            あなたにぴったりな車両をご提案！
          </h1>
          <p className="text-base sm:text-xl md:text-2xl mb-8 sm:mb-10 md:mb-8 leading-relaxed" style={{ fontFamily: 'serif' }}>
            過去の審査で諦めていた方もご相談ください。<br className="hidden sm:block" />
            私たちは、あなたの「現在」と「これから」を応援します。
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-8 sm:mb-10 md:mb-10 max-w-xs sm:max-w-none mx-auto">
            <div className="bg-white text-gray-900 px-3 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg border-2 border-orange-200 text-sm sm:text-lg w-full sm:w-auto text-center" style={{ fontFamily: 'serif' }}>
              高品質
            </div>
            <div className="bg-white text-gray-900 px-3 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg border-2 border-orange-200 text-sm sm:text-lg w-full sm:w-auto text-center" style={{ fontFamily: 'serif' }}>
              低価格
            </div>
            <div className="bg-white text-gray-900 px-3 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg border-2 border-orange-200 text-sm sm:text-lg w-full sm:w-auto text-center" style={{ fontFamily: 'serif' }}>
              保証付き
            </div>
            <div className="bg-white text-gray-900 px-3 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg border-2 border-orange-200 text-sm sm:text-lg w-full sm:w-auto text-center" style={{ fontFamily: 'serif' }}>
              全国配送可能
            </div>
          </div>
          <button 
            onClick={openContactForm}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 sm:px-8 md:px-12 py-3 md:py-4 rounded-full text-sm sm:text-lg md:text-xl font-bold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer max-w-[90%] mx-auto leading-tight"
            style={{ fontFamily: 'serif' }}
          >
            【無料】在庫確認・お問い合わせはこちら
            <i className="ri-arrow-right-line ml-2 md:ml-3"></i>
          </button>
        </div>
      </section>

      {/* Problem/Empathy Section */}
      <section className="py-16 md:py-20 bg-white mt-8 sm:mt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'serif' }}>
              こんな理由で、車の購入を諦めていませんか？
            </h2>
            <div className="w-20 h-1 bg-gray-900 mb-8 mx-auto"></div>
            <p className="text-base md:text-lg text-gray-700 leading-loose" style={{ fontFamily: 'serif' }}>
              「仕事や送り迎えでどうしても車が必要なのに、他店でローンの審査が通らなかった…」「中古車は故障が怖くて、どこで買えばいいか分からない」そんな不安を抱える方は少なくありません。私たちは、数字だけの審査ではなく、お客様一人ひとりのご事情に寄り添った最適な一台をご提案します。
            </p>
          </div>
        </div>
      </section>

      {/* Strengths Section */}
      <section id="strengths" className="py-16 md:py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12 md:mb-16" style={{ fontFamily: 'serif' }}>
            アドバンスポットの「4つの強み」
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-earth-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                全国対応
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                お住まいの地域に関わらず、全国どこへでもご希望のお車をお届けします。
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                プロの目利きによる仕入れ
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                走行性能・外装・内装のすべてにおいて厳しい基準をクリアした車両を厳選。
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                独自の『未来志向』審査
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                過去の信用情報に縛られず、現在の収入や今後の支払いプランを重視する提携ローンを採用。
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                一人ひとりに合わせた支払いプラン
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                お客様のライフスタイルや収支に合わせた無理のないプランをご提案。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Warranty Section */}
      <section id="warranty" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'serif' }}>
              中古車だからこそ、妥協のない安心保障
            </h2>
            <p className="text-lg md:text-xl text-gray-700" style={{ fontFamily: 'serif' }}>
              万が一のトラブルにも備え、充実したアフター保証をご用意しています。
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-orange-50 p-8 rounded-lg text-center">
              <div className="mb-6">
                <i className="ri-shield-check-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                最長10年保証
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                エンジン・電装系など重要な部品にも対応。
              </p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg text-center">
              <div className="mb-6">
                <i className="ri-task-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                修理歴チェック済
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                すべての車両は、納車前に第三者機関がチェック。
              </p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg text-center">
              <div className="mb-6">
                <i className="ri-arrow-go-back-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                返品OK！
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'serif' }}>
                一定条件のもと、国産車100日、輸入車30日以内の返品が可能。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lineup & Contact */}
      <section id="contact" className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12 md:mb-16" style={{ fontFamily: 'serif' }}>
            取扱い車種ラインナップ
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="bg-white p-6 rounded-lg text-center shadow-sm">
              <div className="mb-4 w-full h-32">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20hybrid%20sedan%20car%20front%20three%20quarter%20view%20clean%20white%20background%20professional%20automotive%20photography%20fuel%20efficient%20vehicle&width=400&height=300&seq=003&orientation=landscape"
                  alt="ハイブリッド"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="mb-4">
                <i className="ri-leaf-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                ハイブリッド
              </h3>
            </div>
            
            <div className="bg-white p-6 rounded-lg text-center shadow-sm">
              <div className="mb-4 w-full h-32">
                <img 
                  src="https://readdy.ai/api/search-image?query=small%20compact%20car%20hatchback%20front%20view%20clean%20white%20background%20professional%20automotive%20photography%20city%20car%20efficient&width=400&height=300&seq=004&orientation=landscape"
                  alt="コンパクトカー"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="mb-4">
                <i className="ri-car-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                コンパクトカー
              </h3>
            </div>
            
            <div className="bg-white p-6 rounded-lg text-center shadow-sm">
              <div className="mb-4 w-full h-32">
                <img 
                  src="https://readdy.ai/api/search-image?query=japanese%20kei%20car%20mini%20vehicle%20small%20cute%20car%20front%20view%20clean%20white%20background%20professional%20automotive%20photography%20lightweight&width=400&height=300&seq=005&orientation=landscape"
                  alt="軽自動車"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="mb-4">
                <i className="ri-taxi-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                軽自動車
              </h3>
            </div>
            
            <div className="bg-white p-6 rounded-lg text-center shadow-sm">
              <div className="mb-4 w-full h-32">
                <img 
                  src="https://readdy.ai/api/search-image?query=family%20minivan%20seven%20seater%20vehicle%20front%20view%20clean%20white%20background%20professional%20automotive%20photography%20spacious%20family%20car&width=400&height=300&seq=006&orientation=landscape"
                  alt="ミニバン"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="mb-4">
                <i className="ri-bus-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                ミニバン
              </h3>
            </div>
            
            <div className="bg-white p-6 rounded-lg text-center shadow-sm">
              <div className="mb-4 w-full h-32">
                <img 
                  src="https://readdy.ai/api/search-image?query=SUV%20crossover%20vehicle%20front%20three%20quarter%20view%20clean%20white%20background%20professional%20automotive%20photography%20versatile%20family%20SUV&width=400&height=300&seq=007&orientation=landscape"
                  alt="SUV"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="mb-4">
                <i className="ri-roadster-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                SUV
              </h3>
            </div>
          </div>
          
          <div className="text-center mb-10">
            <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8" style={{ fontFamily: 'serif' }}>
              その他にも沢山の車のご用意がございます。是非一度お問い合わせください！
            </p>
            <button 
              onClick={openContactForm}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 sm:px-8 md:px-12 py-3 md:py-4 rounded-full text-sm sm:text-lg md:text-xl font-bold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer max-w-[90%] mx-auto leading-tight"
              style={{ fontFamily: 'serif' }}
            >
              【無料】在庫確認・お問い合わせはこちら
              <i className="ri-arrow-right-line ml-2 md:ml-3"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Customer Voice */}
      <section id="voice" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
              実際にご利用いただいたお客様の声
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-orange-50 p-8 rounded-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-xl text-gray-600"></i>
                </div>
                <span className="text-sm text-gray-600" style={{ fontFamily: 'serif' }}>30代・男性</span>
              </div>
              <div className="mb-4">
                <i className="ri-double-quotes-l text-3xl text-gray-900"></i>
              </div>
              <p className="text-lg text-gray-900 leading-relaxed" style={{ fontFamily: 'serif' }}>
                ローンが通らず困っていた時に相談に乗ってもらい、無事に希望の車を手にできました
              </p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-xl text-gray-600"></i>
                </div>
                <span className="text-sm text-gray-600" style={{ fontFamily: 'serif' }}>40代・女性</span>
              </div>
              <div className="mb-4">
                <i className="ri-double-quotes-l text-3xl text-gray-900"></i>
              </div>
              <p className="text-lg text-gray-900 leading-relaxed" style={{ fontFamily: 'serif' }}>
                丁寧な説明で、中古車の不安がなくなりました。毎日快適に運転しています
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12 md:mb-16" style={{ fontFamily: 'serif' }}>
            ご利用の流れ
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <span className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'serif' }}>1</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                  お問い合わせ
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif' }}>
                  まずは、お電話・お問い合わせフォームのいずれかからご相談ください。 「こんな車が欲しい」「予算はこれくらい」「ローン審査が心配」など、ざっくりしたご要望でも大丈夫です。 スタッフが丁寧にヒアリングし、最適なご提案をいたします。
                </p>
                <div className="flex justify-center sm:justify-start">
                  <i className="ri-phone-line text-2xl md:text-3xl text-gray-900"></i>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <span className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'serif' }}>2</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                  車両提案・審査
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif' }}>
                  実際のお車をご覧いただき、状態・装備・価格をご案内します。 遠方の方や忙しい方は、写真や動画でのご確認もOK。 お見積りは無料ですので、比較してじっくり検討いただけます。内容にご納得いただけましたら、ご契約へ進みます。 ローンをご希望の方は、その場で仮審査も可能。 わからないことはひとつずつご説明しますのでご安心ください。
                </p>
                <div className="flex justify-center sm:justify-start">
                  <i className="ri-car-line text-2xl md:text-3xl text-gray-900"></i>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <span className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'serif' }}>3</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                  ご契約・書類準備
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif' }}>
                  自動車の登録・名義変更に必要な書類をご用意いただきます。 提出方法や書き方はスタッフがしっかりサポートいたします。 初めて車を買う方でもスムーズに進められるよう、丁寧にご案内します。
                </p>
                <div className="flex justify-center sm:justify-start">
                  <i className="ri-file-text-line text-2xl md:text-3xl text-gray-900"></i>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <span className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'serif' }}>4</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
                  点検・納車
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif' }}>
                  整備・点検・クリーニングを行い、ベストな状態で納車いたします。 店舗でのお受け取りはもちろん、遠方の方には全国納車にも対応しています。 納車後の保証やアフターサービスもお任せください。
                </p>
                <div className="flex justify-center sm:justify-start">
                  <i className="ri-truck-line text-2xl md:text-3xl text-gray-900"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12 md:mb-16" style={{ fontFamily: 'serif' }}>
            よくある質問
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            <div className="bg-orange-50 p-8 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-white font-bold" style={{ fontFamily: 'serif' }}>Q</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                  遠方でも購入可能ですか？
                </h3>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-white font-bold" style={{ fontFamily: 'serif' }}>A</span>
                </div>
                <p className="text-lg text-gray-900 leading-relaxed" style={{ fontFamily: 'serif' }}>
                  はい、全国配送に対応しております。オンライン商談・契約にも対応しておりますので詳細はお問い合わせください。
                </p>
              </div>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-white font-bold" style={{ fontFamily: 'serif' }}>Q</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                  提携ローンに金利はかかりますか？
                </h3>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-white font-bold" style={{ fontFamily: 'serif' }}>A</span>
                </div>
                <p className="text-lg text-gray-900 leading-relaxed" style={{ fontFamily: 'serif' }}>
                  独自のプランをご案内しております。無理のないお支払い計画を一緒に作成しましょう。まずはご相談ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 md:mb-8 leading-tight" style={{ fontFamily: 'serif' }}>
            気になる車がございましたらお気軽にお問い合わせください！
          </h2>
          
          <div className="mb-6 md:mb-8">
            <button 
              onClick={openContactForm}
              className="bg-white text-orange-600 hover:bg-gray-50 px-4 sm:px-8 md:px-12 py-3 md:py-4 rounded-full text-sm sm:text-lg md:text-xl font-bold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer max-w-[90%] mx-auto leading-tight"
              style={{ fontFamily: 'serif' }}
            >
              在庫確認・お問い合わせはこちら
              <i className="ri-arrow-right-line ml-2 md:ml-3"></i>
            </button>
          </div>
          
          <div className="text-white text-center">
            <p className="text-base md:text-lg mb-2" style={{ fontFamily: 'serif' }}>
              簡単1分/無料
            </p>
            <p className="text-sm opacity-90" style={{ fontFamily: 'serif' }}>
              お問い合わせ後、2営業日以内にご連絡します。
            </p>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-12 md:py-16 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <i className="ri-earth-line text-6xl md:text-9xl absolute top-10 right-10"></i>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8" style={{ fontFamily: 'serif' }}>
                アドバンスポット
              </h2>
              <div className="space-y-2 md:space-y-3 text-sm leading-loose" style={{ fontFamily: 'serif' }}>
                <p><strong>商号：</strong>合同会社アドバンスポット</p>
                <p><strong>会社法人等番号：</strong>1400-03-013295</p>
                <p><strong>住所：</strong>兵庫県神戸市兵庫区荒田町一丁目16番8号 湊川センタービルA棟701号</p>
                <p><strong>代表：</strong>坪田正美</p>
                <p><strong>会社成立：</strong>平成30年9月10日</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <button 
                onClick={() => setShowContactForm(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 md:px-8 py-2 md:py-3 rounded-full text-base md:text-lg font-bold mb-4 md:mb-6 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'serif' }}
              >
                お問い合わせ
              </button>
              <div className="flex justify-center md:justify-end space-x-3 md:space-x-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-facebook-line text-lg md:text-xl"></i>
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-twitter-line text-lg md:text-xl"></i>
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-instagram-line text-lg md:text-xl"></i>
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-line-line text-lg md:text-xl"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 md:mt-12 pt-6 md:pt-8 border-t border-white/20 text-xs md:text-sm space-y-4 sm:space-y-0">
            <p style={{ fontFamily: 'serif' }}>© 2025 アドバンスポット All Rights Reserved.</p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 text-center">
              <a href="#" className="hover:text-orange-400 transition-colors" style={{ fontFamily: 'serif' }}>プライバシーポリシー</a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
                  お問い合わせフォーム
                </h3>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'serif' }}>
                    お問い合わせありがとうございます
                  </h4>
                  <p className="text-gray-600 mb-8" style={{ fontFamily: 'serif' }}>
                    2営業日以内にご連絡いたします。
                  </p>
                  <button
                    onClick={() => setShowContactForm(false)}
                    className="px-8 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'serif' }}
                  >
                    閉じる
                  </button>
                </div>
              ) : (
              <form id="contact-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-4 md:space-y-6">
                <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                      お名前<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                      style={{ fontFamily: 'serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                      ふりがな<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="furigana"
                      required
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                      style={{ fontFamily: 'serif' }}
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                      電話番号<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                      style={{ fontFamily: 'serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                      メールアドレス<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                      style={{ fontFamily: 'serif' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                    ご希望の車種
                  </label>
                  <select
                    name="car_type"
                    className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                    style={{ fontFamily: 'serif' }}
                  >
                    <option value="">選択してください</option>
                    <option value="ハイブリッド">ハイブリッド</option>
                    <option value="コンパクトカー">コンパクトカー</option>
                    <option value="軽自動車">軽自動車</option>
                    <option value="ミニバン">ミニバン</option>
                    <option value="SUV">SUV</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                    ご予算
                  </label>
                  <select
                    name="budget"
                    className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                    style={{ fontFamily: 'serif' }}
                  >
                    <option value="">選択してください</option>
                    <option value="50万円以下">50万円以下</option>
                    <option value="50万円〜100万円">50万円〜100万円</option>
                    <option value="100万円〜150万円">100万円〜150万円</option>
                    <option value="150万円〜200万円">150万円〜200万円</option>
                    <option value="200万円以上">200万円以上</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'serif' }}>
                    お問い合わせ内容<span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
                    style={{ fontFamily: 'serif' }}
                    placeholder="ご質問やご要望をお書きください（500文字以内）"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'serif' }}>※500文字以内でご入力ください</p>
                </div>

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
                    <i className="ri-error-warning-line text-red-500 mr-3 text-lg"></i>
                    <p className="text-sm text-red-700" style={{ fontFamily: 'serif' }}>
                      送信エラーが発生しました。もう一度お試しください。
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'serif' }}
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full sm:w-auto px-6 md:px-8 py-2 md:py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'serif' }}
                  >
                    {formStatus === 'submitting' ? '送信中...' : '送信する'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;